import { useEffect, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { CableIcon, Settings } from "lucide-react";
import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import backend from "../../../../../backend/backend";
import Notify from "../../../../shared/Notify";
import Spinner from "../../../../../assets/icons/Spinner";
import PlusIcon from "../../../../../assets/icons/PlusIcon";
import LogoConfluence from "../../../../../assets/icons/LogoConfluence";
import TrashIcon from "../../../../../assets/icons/TrashIcon";
import SelectIntegrationDialog from "./SelectIntegrationDialog";
import ConfluenceContentPanel from "./confluence/ConfluenceContentPanel";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import "./IntegrationsContentPanel.scss";

dayjs.extend(relativeTime);

const IntegrationsContentPanel = ({ kbase, workspaceId }) => {
  const intl = useIntl();
  const [currentView, setCurrentView] = useState("overview");
  const [integrationConfig, setIntegrationConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadConfig = useCallback(() => {
    setLoading(true);
    backend
      .getKBaseIntegrationConfig(workspaceId, kbase.uid)
      .then((data) => {
        setIntegrationConfig(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load integration config", err);
        setIntegrationConfig(null);
        setLoading(false);
      });
  }, [workspaceId, kbase]);

  useEffect(() => {
    if (kbase && workspaceId) {
      loadConfig();
    }
  }, [kbase, workspaceId, loadConfig]);

  const handleIntegrationConfigured = () => {
    setShowSelectDialog(false);
    loadConfig();
    setCurrentView("confluence");
  };

  const handleRemoveIntegration = () => {
    setIsDeleting(true);
    backend
      .removeKBaseIntegration(workspaceId, kbase.uid)
      .then(() => {
        Notify.success(label(intl, "kbase_integration_removed"));
        setShowDeleteConfirm(false);
        setIsDeleting(false);
        loadConfig();
      })
      .catch((err) => {
        console.error("Failed to remove integration", err);
        Notify.error(label(intl, "error_unexpected"));
        setIsDeleting(false);
        setShowDeleteConfirm(false);
      });
  };

  const parseMetadata = (metadataJson) => {
    if (!metadataJson) return null;
    try {
      return JSON.parse(metadataJson);
    } catch (e) {
      console.error("Failed to parse integration metadata", e);
      return null;
    }
  };

  if (currentView === "confluence" && integrationConfig?.integrationMetadata) {
    return (
      <ConfluenceContentPanel
        kbase={kbase}
        workspaceId={workspaceId}
        integrationConfig={integrationConfig}
        onBack={() => {
          setCurrentView("overview");
          loadConfig();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="integrations_content_panel loading">
        <Spinner size={40} />
      </div>
    );
  }

  const metadata = parseMetadata(integrationConfig?.integrationMetadata);
  const hasIntegration =
    metadata && integrationConfig?.integrationConnectionUid;

  return (
    <div className="integrations_content_panel">
      {!hasIntegration && (
        <div className="empty_state">
          <div className="empty_icon">
            <CableIcon size={64} color="#67748e" />
          </div>
          <h3>
            <Message id="kbase_integration_empty_title" />
          </h3>
          <p>
            <Message id="kbase_integration_empty_message" />
          </p>
          <button
            className="btn bg-gradient-dark"
            onClick={() => setShowSelectDialog(true)}
          >
            <PlusIcon color="#fff" strokeWidth="2" size="18" />
            <Message id="kbase_integration_add" />
          </button>
        </div>
      )}

      {hasIntegration && (
        <div className="integration_overview">
          <div
            className="integration_card"
            onClick={() => setCurrentView("confluence")}
          >
            <div className="integration_card_logo">
              <LogoConfluence size={48} />
            </div>
            <div className="integration_card_content">
              <div className="integration_type">
                <Message id="integration_confluence" />
              </div>
              <div className="integration_url">{metadata.pageUrl}</div>
              {integrationConfig.lastSyncAt && (
                <div className="integration_last_sync">
                  <Message id="kbase_integration_last_sync" />:{" "}
                  {dayjs(integrationConfig.lastSyncAt).fromNow()}
                </div>
              )}
            </div>
            <div
              className="integration_card_actions"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="btn btn-outline-dark btn-sm"
                onClick={() => setCurrentView("confluence")}
              >
                <Settings size={16} color="#344767" />
                <Message id="kbase_integration_configure" />
              </button>
              <div className="confirm_container">
                {showDeleteConfirm && (
                  <>
                    <div
                      className="cover"
                      onClick={() => setShowDeleteConfirm(false)}
                    ></div>
                    <div className="confirm_panel">
                      <button
                        className="btn bg-gradient-primary btn-sm"
                        onClick={handleRemoveIntegration}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Spinner size={16} color="#fff" />
                        ) : (
                          <Message id="confirm_delete" />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
              >
                <TrashIcon size={16} color="#dc3545" />
                <Message id="kbase_integration_remove" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showSelectDialog && (
        <SelectIntegrationDialog
          kbase={kbase}
          workspaceId={workspaceId}
          onClose={() => setShowSelectDialog(false)}
          onSuccess={handleIntegrationConfigured}
        />
      )}
    </div>
  );
};

export default IntegrationsContentPanel;
